import { useSelectedConversation } from "@/contexts/SelectedConversationContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoUser } from "@/models/LazoUser";
import LazoUserService from "@/services/LazoUserService";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { AddOutlined, ReplayOutlined } from "@mui/icons-material";
import { Box, Checkbox, Typography, styled } from "@mui/material";
import { ApiError } from "next/dist/server/api-utils";
import { CSSProperties, RefObject, useEffect, useState } from "react";
import CommonButton, { CommonButtonSecondarySx } from "../utils/CommonButton";
import CommonTextField from "../utils/CommonTextField";
import LazoModal from "../utils/LazoModal";
import SearchField from "../utils/SearchField";
import UserAvatar from "./Avatar";

const StyledButton = styled(CommonButton)(() => ({
  backgroundColor: `${Colors.WHITE}`,
  color: `${Colors.LAZO_PURPLE}`,
  border: `1px solid ${Colors.LAZO_PURPLE}`,
  ":hover": {
    backgroundColor: `${Colors.BUTTON_SECONDARY_HOVER_PURPLE}`,
  },
}));

const AvatarStyle: CSSProperties = {
  width: "2rem",
  height: "2rem",
  fontSize: "1rem",
};

const Divider = () => {
  return (
    <div
      style={{
        minHeight: "1px",
        height: "1px",
        width: "100%",
        backgroundColor: `${Colors.LIGHT_GRAY}`,
      }}
    />
  );
};

const StyledTextField = styled(CommonTextField)(({}) => ({
  "& .MuiInputBase-root": {
    height: "2.5rem",
    borderRadius: ".5rem",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
      borderRadius: "0.5rem",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
    },
  },
}));

type ManageUserModalProps = {
  open: boolean;
  handleClose: () => void;
  hasMembers: boolean;
};

function ManageUserModal(props: ManageUserModalProps) {
  const [members, setMembers] = useState<{ lazoUser: LazoUser; toRemove: boolean }[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<LazoUser[]>([]);
  const [userList, setUserList] = useState<LazoUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);
  const { currentTenantMembership } = useTenantMemberships();

  const { selectedConversation, setSelectedConversation } = useSelectedConversation();
  const loggedUser = AuthService.getUser();

  function handleSearch(searchRef: RefObject<HTMLInputElement> | undefined) {
    console.info("searchRef", searchRef);
    if (!currentTenantMembership.tenant) return;
    console.log("currentTenantMembership", currentTenantMembership.tenant.externalId);
    setIsLoading(true);
    if (searchRef?.current?.value !== "") {
      LazoUserService.searchUserByTenantAndTerm(searchRef?.current?.value!, currentTenantMembership.tenant.externalId!)
        .then((r) => {
          const content = r.getContentOrThrowError();
          setUserList(
            content.filter(
              (lazoUser) => selectedConversation?.members?.findIndex((info) => info.user.externalId === lazoUser.user.externalId) === -1
            )
          );
        })
        .catch((e: ApiError) => NotificationService.error(e.message))
        .finally(() => setIsLoading(false));
    } else {
      LazoUserService.getAllByTenantExternalId(currentTenantMembership?.tenant.externalId!)
        .then((r) => {
          setUserList(
            r
              .getContentOrThrowError()
              .filter(
                (lazoUser) => selectedConversation?.members?.findIndex((info) => info.user.externalId === lazoUser.user.externalId) === -1
              )
          );
        })
        .catch((e: ApiError) => NotificationService.error(e.message))
        .finally(() => setIsLoading(false));
    }
  }

  useEffect(() => {
    if (!currentTenantMembership.tenant) {
      return;
    }

    setIsLoading(true);
    LazoUserService.getAllByTenantExternalId(currentTenantMembership?.tenant.externalId!)
      .then((r) => {
        let members: LazoUser[] = [];
        let nonMembers: LazoUser[] = [];
        r.getContentOrThrowError().forEach((el) =>
          (selectedConversation?.members?.findIndex((info) => info.user.externalId === el.user.externalId) !== -1
            ? members
            : nonMembers
          ).push(el)
        );
        const membersWithRemove: { lazoUser: LazoUser; toRemove: boolean }[] = [];
        members.forEach((lazoUser) => membersWithRemove.push({ lazoUser: lazoUser, toRemove: false }));
        setMembers(membersWithRemove);
        setUserList(nonMembers);
      })
      .catch((e: ApiError) => NotificationService.error(e.message))
      .finally(() => setIsLoading(false));
  }, [selectedConversation]);

  function handleCheckbox(event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void {
    if (checked) {
      const user = userList.find((u) => u.externalId === event.currentTarget.value);
      setSelectedUsers([...selectedUsers, user!]);
    } else {
      const newSelectedUsers = selectedUsers.filter((u) => u.externalId !== event.currentTarget.value);
      setSelectedUsers(newSelectedUsers);
    }
  }

  function handleCancel() {
    props.handleClose();
  }

  function handleConfirm() {
    setSavingChanges(true);
    const membersToDelete: LazoUser[] = [];
    members.forEach((member) => {
      if (member.toRemove) {
        membersToDelete.push(member.lazoUser);
      }
    });

    LazoChatConversationService.manageUsersFromConversation(selectedUsers, membersToDelete, selectedConversation!.externalId!)
      .then((r) => {
        const updatedConversation = r.getContentOrThrowError();
        setSelectedConversation(updatedConversation);
        setSelectedUsers([]);
        props.handleClose();
      })
      .catch((e: ApiError) => NotificationService.error(e.message))
      .finally(() => setSavingChanges(false));
  }

  function handleRemove(idx: number): void {
    setMembers(members.map((member, i) => (i === idx ? { ...member, toRemove: true } : member)));
  }

  function hanldeUndo(idx: number): void {
    setMembers(members.map((member, i) => (i === idx ? { ...member, toRemove: false } : member)));
  }

  return (
    <LazoModal
      open={props.open}
      handleClose={props.handleClose}
      title={props.hasMembers ? "Manage Members" : "Add Members"}
      buttons={
        <Box sx={{ display: "flex", width: "100%", gap: "0.5rem" }}>
          <StyledButton onClick={handleCancel} sx={{ width: "50%" }}>
            <Typography sx={{ fontWeight: 600 }}>Cancel</Typography>
          </StyledButton>
          {props.hasMembers ? (
            <CommonButton disabled={savingChanges} sx={{ width: "50%", height: "2.75rem" }} onClick={handleConfirm}>
              <Typography sx={{ fontWeight: 600 }}>Confirm</Typography>
            </CommonButton>
          ) : (
            <CommonButton
              disabled={selectedUsers.length <= 0 || savingChanges}
              sx={{ width: "50%", height: "2.75rem" }}
              onClick={handleConfirm}
            >
              <AddOutlined />
              <Typography>Add</Typography>
            </CommonButton>
          )}
        </Box>
      }
      sx={{ maxWidth: "430px", width: "100%" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {props.hasMembers && (
          <>
            <Box sx={{ display: "flex", width: "100%" }}>
              <Typography sx={{ fontWeight: "500" }}>Added Members</Typography>
            </Box>
            <Box
              sx={{
                paddingTop: "0.2rem",
                display: "flex",
                flexFlow: "column",
                width: "100%",
                gap: "0.8rem",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {members.map((member, idx) => {
                return (
                  <Box
                    key={idx}
                    sx={{
                      minHeight: "30px",
                      height: "30px",
                      display: "flex",
                      flexFlow: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexFlow: "row",
                        alignItems: "center",
                        gap: "0.5rem",
                        opacity: member.toRemove ? 0.5 : 1,
                      }}
                    >
                      <UserAvatar style={{ ...AvatarStyle }} key={idx} user={member.lazoUser.user} />
                      <Box>
                        <Typography sx={{ fontWeight: "500" }}>
                          {member.lazoUser.user.lastName + " " + member.lazoUser.user.firstName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: `${Colors.STONE_GRAY}`,
                          }}
                        >
                          {member.lazoUser.title}
                        </Typography>
                      </Box>
                    </Box>
                    {loggedUser.externalId === member.lazoUser.user.externalId ? null : member.toRemove ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexFlow: "row",
                          gap: "0.5rem",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ color: `${Colors.STONE_GRAY}` }}>Removed</Typography>
                        <StyledButton onClick={() => hanldeUndo(idx)}>
                          <ReplayOutlined sx={{ transform: "rotate(-60deg)" }} />
                          <Typography>Undo</Typography>
                        </StyledButton>
                      </Box>
                    ) : (
                      <StyledButton onClick={() => handleRemove(idx)}>
                        <Typography sx={{ fontWeight: 500 }}>Remove</Typography>
                      </StyledButton>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Divider />
            <Box sx={{ display: "flex", width: "100%" }}>
              <Typography sx={{ fontWeight: "500" }}>Invite via Email</Typography>
            </Box>
          </>
        )}
        {false && (
          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              height: "45px",
              width: "100%",
              "& .MuiOutlinedInput-root": { height: "45px" },
            }}
          >
            <StyledTextField placeholder="Email, comma separated" fullWidth />
            <CommonButton sx={{ ...CommonButtonSecondarySx, borderRadius: "0.5rem" }}>
              <Typography sx={{ fontWeight: "500" }}>Invite</Typography>
            </CommonButton>
          </Box>
        )}
        {!props.hasMembers && <Divider />}
        <SearchField
          triggerSearchOnInputChange={true}
          fullWidth
          handleSearch={handleSearch}
          displaySearchButton={false}
          containerProps={{
            sx: {
              display: "flex",
              gap: "1rem",
              width: "100%",
              "& .MuiInputBase-root": {
                height: "2.5rem",
                borderRadius: ".5rem",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
                  borderRadius: "0.5rem",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
                },
              },
            },
          }}
        />
        <Box
          sx={{
            paddingTop: "1rem",
            display: "flex",
            flexFlow: "column",
            width: "100%",
            gap: "0.45rem",
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Spinner variant="indeterminate" sx={{ color: Colors.LAZO_PURPLE }} />
          ) : (
            userList.map((lazoUser, idx) => {
              return (
                <>
                  <Box
                    key={lazoUser.user.email}
                    sx={{
                      display: "flex",
                      flexFlow: "row",
                      alignItems: "center",
                      minHeight: "30px",
                      height: "30px",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexFlow: "row",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <UserAvatar style={{ ...AvatarStyle }} key={idx} user={lazoUser.user} />
                      <Box>
                        <Typography sx={{ fontWeight: "500" }}>{lazoUser.user.lastName + " " + lazoUser.user.firstName}</Typography>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: `${Colors.STONE_GRAY}`,
                          }}
                        >
                          {lazoUser.title}
                        </Typography>
                      </Box>
                    </Box>
                    {lazoUser.user.externalId === loggedUser.externalId ? (
                      <Typography sx={{ padding: "0 1rem" }}>You</Typography>
                    ) : (
                      <Checkbox
                        sx={{
                          "&.MuiCheckbox-root": {
                            color:
                              selectedUsers.findIndex((u) => u.externalId === lazoUser.externalId) !== -1
                                ? Colors.LAZO_PURPLE
                                : Colors.LIGHT_GRAY,
                            "& .MuiSvgIcon-root": {
                              fontSize: "1.3rem",
                            },
                          },
                        }}
                        checked={selectedUsers.findIndex((u) => u.externalId === lazoUser.externalId) !== -1}
                        value={lazoUser.externalId}
                        onChange={(event, checked) => handleCheckbox(event, checked)}
                      />
                    )}
                  </Box>
                </>
              );
            })
          )}
        </Box>
      </Box>
    </LazoModal>
  );
}

export default ManageUserModal;
